import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import { getMostFrequentStopReasonCodesForMachine } from "../../stopReasonCodes/hooks/fetchers";
import { Box, Button, Paper, Grid, useTheme, Stack, Typography, Divider } from "@mui/material";
import messages from "./messages";
import { useMsal, useAccount } from "@azure/msal-react";
import { patchMachineStop } from "../../../fetchers/machine";
import { pushErrorMessage } from "../../../utils/notifications";
import { tokens } from "../../../assets/theme";
import { pushNotification } from "../../../utils/notifications";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import dayjs from "dayjs";
import { formatMinutesAsDuration } from "../../../utils/durationUtils";
import { fetchCompany } from '../../../fetchers/company';
import { isoFormat, formatDateFullTime, formatDateTime, formatDate, formatTime, formatDateTimeISO } from "../../../utils/dateTimeUtils";      


export default function MachineStopEditDialog(props) {
    const language = localStorage.getItem('language') || 'en'

    const { formatMessage } = useIntl();
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const queryClient = useQueryClient();

    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    let locale = (language === "en") ? 'en-US' : 'sv-SE';

    const [machineStopPart, setMachineStopPart] = useState({
        Id: props.machineStop.id,
        stopReasonCodeId: props.machineStop.stopReasonCodeId,
        comment: props.machineStop.comment,
        updatedBy: account.username,
        commentedBy: account.username
    });

    const mostFrequentStopReasonCodesQuery = useQuery(
        ["mostFrequentStopReasonCodes", props.machineStop.machineId],
        () => getMostFrequentStopReasonCodesForMachine(props.machineStop.machineId),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 120_000,
            refetchInterval: 120_000,
            refetchIntervalInBackground: true,
        }
    );

    const companyQuery = useQuery({
            queryKey:  ["company", companyKey],
            queryFn: () => fetchCompany(companyKey),
            staleTime: 60000,
            refetchInterval: 60000,
        });

    const handleChange = (event) => {
        const { id, name, value, type, checked } = event.target
        setMachineStopPart(prev => {
            return {
                ...prev,
                [id ? id : name]: type === "checkbox" ? checked : value
            }
        })
    }

    const handleSave = () => {
        props.setShowBackdrop(true);
        const machineStopPartToSave = machineStopPart
        if (machineStopPartToSave.comment && machineStopPart.comment.length > 0) {
            machineStopPartToSave.commentedBy = account.username
        } else {
            machineStopPartToSave.commentedBy = null
        }
        setMachineStopPart(machineStopPartToSave);
        patchMachineStop(machineStopPartToSave).then((response) => {
            queryClient.invalidateQueries(["uncodedStops"], { refetchType: "all" });
            props.refresh();
            props.setOpenPopup(false);
            props.setShowBackdrop(false);
            if (response && response.activityTriggered) {
                pushNotification(<FormattedMessage {...messages.activityTriggered} />);
                props.refetchActivities();
            }
        })
            .catch((error) => {
                console.log(error);
                props.setShowBackdrop(false);
                pushErrorMessage(`Could not update the stop.`);
            });
    }

    const handleSplit = () => {
        props.setOpenPopup(false);
        props.handleSplit(machineStopPart.Id);
    }

    function formatDate(date) {
        locale = (companyKey === 2) ? 'en-US' : 'sv-SE';
        return date ? new Date(date).toLocaleDateString(locale) : "";
    }
    function formatFullTime(date) {
        return date ? new Date(date).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "";
    }

    function formatFullDateTime(date) {
        return date ? formatDateFullTime(language, date) : "";
    }

    function convertTZ(date, tzString) {
        return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", { timeZone: tzString }));
    }
    function getUSDateAndTime(date) {
        return convertTZ(date, companyQuery.data?.timezone ?? "America/New_York");
    }

    const createdby = props.machineStop.commentCreatedBy != null ? "Last updated by: " + props.machineStop.commentCreatedBy : "";
    const updatedDate = props.machineStop.commentCreatedBy != null ? formatFullDateTime(props.machineStop.updatedDate) : "";

    const quickPicks = (mostFrequentStopReasonCodesQuery?.data || []).slice(0, 6);
    const undefinedStopReasonCodeIds = (props.stopReasonCodes || [])
        .filter((item) => item?.isUndefined)
        .map((item) => item.id);

    const handleQuickSelect = (stopReasonCodeId) => {
        setMachineStopPart((prev) => ({
            ...prev,
            stopReasonCodeId
        }));
    };

    if (companyQuery.isLoading) {
        return "";
    }

    return (
        <>
            <Box
                sx={{
                    p: 1,
                    m: 1
                }}>
                {quickPicks.length > 0 && (
                    <Box sx={{ mb: 2.25 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
                            <FormattedMessage {...messages.quickPicks} />
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                gap: 1.25,
                            }}
                        >
                            {quickPicks.map((code) => {
                                const selected = machineStopPart.stopReasonCodeId === code.id;

                                return (
                                    <Button
                                        key={code.id}
                                        onClick={() => handleQuickSelect(code.id)}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            height: 52,
                                            borderRadius: 0,
                                            textTransform: "none",
                                            justifyContent: "flex-start",
                                            px: 1.75,
                                            gap: 1.25,

                                            fontSize: "0.9rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.01em",

                                            backgroundColor: "rgba(255,255,255,0.06)",
                                            borderColor: "rgba(255,255,255,0.14)",
                                            color: "rgba(255,255,255,0.90)",

                                            "&:hover": {
                                                backgroundColor: "rgba(255,255,255,0.10)",
                                                borderColor: "rgba(255,255,255,0.22)",
                                            },

                                            ...(selected
                                                ? {
                                                    backgroundColor: "rgba(255,255,255,0.14)",
                                                    borderColor: "rgba(255,255,255,0.34)",
                                                    color: "rgba(255,255,255,0.97)",
                                                    boxShadow: "0 0 0 1px rgba(255,255,255,0.10) inset",
                                                }
                                                : null),
                                        }}
                                    >
                                        {code.colorCode && (
                                            <Box
                                                sx={{
                                                    width: "20px",
                                                    height: "20px",
                                                    borderRadius: 1,
                                                    backgroundColor: code.colorCode,
                                                    border: "1px solid rgba(255,255,255,0.6)",
                                                    flex: "0 0 auto",
                                                }}
                                            />
                                        )}

                                        <Box
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                lineHeight: 1.1,
                                            }}
                                            title={code.name}
                                        >
                                            {code.name}
                                        </Box>
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                )}


                <Box sx={{ marginTop: 2, width: 400 }}>
                    <AxionControls.AxionSelect
                        options={props.stopReasonCodes.map((item) => {
                            return {
                                id: item.id,
                                title: item.name,
                                color: item.colorCode,
                                order: item.sortOrder,
                                group: item.stopReasonCodeGroup ? item.stopReasonCodeGroup.name : null,
                                groupOrder: item.stopReasonCodeGroup ? item.stopReasonCodeGroup.sortOrder : null
                            }
                        }
                        )}
                        showNoneOption={false}
                        name="stopReasonCodeId"
                        label={<FormattedMessage {...messages.stopReasonCode} />}
                        value={machineStopPart.stopReasonCodeId}
                        onChange={handleChange}
                        othersText={<FormattedMessage {...messages.otherStopReasonCodes} />}
                        searchField={true}
                        searchFieldPlaceholder={formatMessage({ ...messages.searchStopReasonCodes })}
                        priorityOptionIds={undefinedStopReasonCodeIds}
                        groupsInitiallyCollapsed={true}
                    />
                </Box>
                <Box>
                    <AxionControls.AxionTextBoxMultiline
                        name="comment"
                        label={<FormattedMessage {...messages.comment} />}
                        value={machineStopPart.comment}
                        onChange={handleChange}
                    />
                </Box>
                <Paper
                    sx={{
                        marginTop: "20px",
                        position: "relative",
                        borderRadius: "4px",
                        backgroundColor: colors.primary[700],
                    }}
                >
                    <Grid container p={2}>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.fromDate} />:
                        </Grid>
                        <Grid item xs={10}>
                            {(companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows) ? formatFullDateTime(props.machineStop?.fromDateUs) : formatFullDateTime(props.machineStop?.fromDate)}
                        </Grid>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.toDate} />:
                        </Grid>
                        <Grid item xs={10}>
                            {(companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows) ? formatFullDateTime(props.machineStop?.toDateUs) : formatFullDateTime(props.machineStop?.toDate)}
                        </Grid>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.duration} />:
                        </Grid>
                        <Grid item xs={10}>
                            {formatMinutesAsDuration(props.machineStop.minutesDown)}
                        </Grid>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.item} />:
                        </Grid>
                        <Grid item xs={10}>
                            {props.machineStop.itemId}
                        </Grid>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.productionOrder} />:
                        </Grid>
                        <Grid item xs={10}>
                            {props.machineStop.productionOrderId}
                        </Grid>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.updated} />:
                        </Grid>
                        <Grid item xs={10}>
                            {(companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows) ? formatFullDateTime(getUSDateAndTime(props.machineStop?.updatedDate)) : formatFullDateTime(props.machineStop?.updatedDate)}
                        </Grid>
                        <Grid item xs={2}>
                            <FormattedMessage {...messages.updatedBy} />:
                        </Grid>
                        <Grid item xs={10}>
                            {props.machineStop.updatedBy}
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1,
                    m: 1
                }}>
                <Box sx={{ flexDirection: 'row' }}>
                    <Button
                        variant="contained"
                        sx={{ marginRight: 2 }}
                        onClick={handleSave}>
                        <FormattedMessage {...messages.save} />
                    </Button>

                    {
                        // companyKey === 1 && 
                        <Button
                            variant="contained"
                            onClick={handleSplit}>
                            <FormattedMessage {...messages.splitStop} />
                        </Button>
                    }
                </Box>
                <Box justify="flex-end" display="flex" alignItems="center">
                    <Box paddingLeft={1}>{createdby}</Box>
                    <Box paddingLeft={1}>{updatedDate}</Box>
                </Box>
            </Box>
        </>
    )
}